import * as React from 'react';
import { Box, Typography, Paper, Grid, Avatar, Button, Link } from '@mui/material';
import InformationalLayout from './InformationalLayout';

function renderBio(bio) {
    if (typeof bio === 'string') {
        return bio;
    }
    return bio.map((part, index) => {
        if (typeof part === 'string') {
            return part;
        }
        return (
            <Link
                key={index}
                href={part.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#1976d2' }}
            >
                {part.text}
            </Link>
        );
    });
}

function About() {
    const team = [
        {
            name: 'Dave Cavaletto',
            role: 'Technology & Operations',
            bio: 'I like bikes. Computers are ok too.',
            avatar: '/team/dave.png'
        },
        {
            name: 'Samuel Thompson',
            role: 'Marketing & Partnerships',
            bio: [
                'Samuel has been hooked on riding very long distances since 2015, sometimes competing in self-supported bikepacking races and other times using the bicycle as a means to see and experience new places. In June 2025, he set the fastest recorded time for the ',
                { text: 'Tour de France Randonneur', href: 'https://www.usmetrocyclotourisme.fr/tdf-2025' },
                ', riding 4,750km around the perimeter of France in 12 days, 15 hours. Some suspect he only really cycles to visit bakeries in diverse locations and feed his croissant addiction. He now combines his experience with a science-led approach to help others achieve their goals in ultra-distance endeavours via his coaching & consulting enterprise, ',
                { text: 'Acier', href: 'https://www.acier.cc' },
            ],
            avatar: '/team/sam.png'
        }
    ];


    return (
        <InformationalLayout
            title="About CueMeUp"
            subtitle="We're passionate adventurers building tools to make your outdoor experiences better"
        >

            {/* Mission Section */}
            <Paper sx={{ p: 6, mb: 6, backgroundColor: 'rgba(8, 3, 3, 0.95)' }}>
                <Typography variant="h4" sx={{ color: '#2c3e50', mb: 4, fontWeight: 600, textAlign: 'center' }}>
                    Our Mission
                </Typography>
                <Typography variant="h6" sx={{ color: '#6c757d', textAlign: 'center', maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}>
                    CueMeUp was born from a simple problem: planning multi-day cycling adventures was unnecessarily complicated.
                    We believe that technology should make outdoor adventures more accessible, not more complex. Our mission is to
                    create intuitive tools that help adventurers of all levels plan, navigate, and enjoy their journeys to the fullest.
                </Typography>
            </Paper>

            {/* Team Section */}
            <Paper sx={{ p: 6, mb: 6, backgroundColor: 'rgba(255,255,255,0.95)' }}>
                <Typography variant="h4" sx={{ color: '#2c3e50', mb: 4, fontWeight: 600, textAlign: 'center' }}>
                    Meet the Team
                </Typography>
                <Grid container spacing={4} justifyContent="center">
                    {team.map((member, index) => (
                        <Grid item xs={12} sm={6} md={5} key={index}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Avatar
                                    src={member.avatar}
                                    variant="square"
                                    sx={{
                                        width: 300,
                                        height: 300,
                                        mx: 'auto',
                                        mb: 3,
                                        '& .MuiAvatar-img': {
                                            objectFit: 'contain'
                                        }
                                    }}
                                >
                                    {member.name.split(' ').map(n => n[0]).join('')}
                                </Avatar>
                                <Typography variant="h5" sx={{ color: '#2c3e50', mb: 1, fontWeight: 600 }}>
                                    {member.name}
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#FF5722', mb: 2 }}>
                                    {member.role}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                    {renderBio(member.bio)}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Story Section */}
            {/* <Paper sx={{ p: 6, mb: 6, backgroundColor: 'rgba(255,255,255,0.95)' }}>
                    <Typography variant="h4" sx={{ color: '#2c3e50', mb: 4, fontWeight: 600, textAlign: 'center' }}>
                        Our Story
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#6c757d', mb: 3, lineHeight: 1.8, textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                        CueMeUp started as a weekend project during a 3-day cycling tour through the Pacific Northwest.
                        Frustrated with the time spent manually creating cue cards and the risk of getting lost, our founder
                        Sam decided to build a better solution.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#6c757d', mb: 3, lineHeight: 1.8, textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                        What began as a simple tool for personal use quickly grew into something bigger. Friends and fellow
                        cyclists started asking for access, and we realized we had built something that could help the entire
                        cycling community.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#6c757d', lineHeight: 1.8, textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                        Today, CueMeUp serves thousands of adventurers worldwide, from weekend warriors to professional
                        tour organizers. We're proud to be part of the outdoor community and committed to making adventure
                        planning accessible to everyone.
                    </Typography>
                </Paper> */}

            {/* Partnership Section */}
            <Paper sx={{ p: 6, mb: 6, backgroundColor: 'rgba(255,255,255,0.9)' }}>
                <Typography variant="h4" sx={{ color: '#2c3e50', mb: 4, fontWeight: 600, textAlign: 'center' }}>
                    In Partnership With
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                    <Box
                        component="img"
                        src="/acier_logo_full.jpg"
                        alt="Acier.cc Logo"
                        sx={{
                            height: 120,
                            mb: 4,
                            maxWidth: '100%',
                            objectFit: 'contain'
                        }}
                    />
                    <Typography variant="body1" sx={{ color: '#6c757d', mb: 4, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}>
                        CueMeUp is partnered with Acier Coaching & Consulting, an enterprise run by Samuel Thompson specialising in working with cyclists with goals in ultra-distance self-supported endeavours.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#6c757d', mb: 4, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}>
                        Obstacles to success in these situations surpass the purely physical. Proper preparation for all aspects is crucial whether racing, participating in brevets or touring. This forms part of Acier's all-encompassing approach to training and preparing for self-supported endeavours.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#6c757d', mb: 4, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}>
                        Acier athletes have been some of the early adopters of CueMeUp both in the preparation stage, and during events to assist with executing their strategy or adapt to unexpected or unforeseen circumstances.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#6c757d', mb: 4, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}>
                        By partnering with CueMeUp, Samuel and Acier hope to promote how better preparation can result in an enhanced experience in almost all situations and add to the enjoyment of outdoor pursuits.
                    </Typography>
                    <Button
                        component="a"
                        href="https://acier.cc"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        sx={{
                            borderColor: '#2c3e50',
                            color: '#2c3e50',
                            '&:hover': {
                                borderColor: '#2c3e50',
                                backgroundColor: 'rgba(44, 62, 80, 0.1)'
                            }
                        }}
                    >
                        Visit Acier.cc
                    </Button>
                </Box>
            </Paper>
        </InformationalLayout>
    );
}

export default About;
